// frontend/src/components/Payroll/PayslipPreviewModal.js
/**
 * Step 4 payslip preview.
 *
 * Lets HR/Finance see exactly what a worker's MOM-itemised payslip PDF will
 * look like BEFORE releasing payslips to workers. Reuses the same
 * `payslip-pdf` endpoint workers see on their own portal, so the preview is
 * byte-identical to what gets released.
 *
 * Props:
 *   isOpen       - boolean
 *   workers      - [{ id, name, staff_id, net_pay }] included workers in the run
 *   onClose      - dismiss callback
 */
import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Download, FileText, Loader2 } from 'lucide-react';
import Modal from '../UI/Modal';
import PdfViewer from '../UI/PdfViewer';
import InfoBadge from '../UI/InfoBadge';
import { confidentialAPI } from '../../services/api';
import toast from '../../utils/toast';

const PayslipPreviewModal = ({ isOpen, workers = [], onClose }) => {
  const [selectedId, setSelectedId] = useState('');
  const [iframeSrc, setIframeSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const sortedWorkers = useMemo(
    () => [...workers].sort((a, b) => (a.staff_id || '').localeCompare(b.staff_id || '')),
    [workers],
  );

  // Pick the first worker when the modal opens
  useEffect(() => {
    if (isOpen && !selectedId && sortedWorkers.length > 0) {
      setSelectedId(sortedWorkers[0].id);
    }
    if (!isOpen) {
      setSelectedId('');
      setIframeSrc(null);
    }
  }, [isOpen, sortedWorkers, selectedId]);

  // Mint a short-lived signed token and load the PDF inline. Serving via a real
  // URL (not a blob) means the browser PDF viewer's own download button names
  // the file from Content-Disposition (Payslip_Preview_<id>_<period>.pdf)
  // instead of a blob UUID.
  useEffect(() => {
    if (!isOpen || !selectedId) return;
    let cancelled = false;

    const loadInline = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await confidentialAPI.getPayslipPreviewToken(selectedId);
        if (cancelled) return;
        setIframeSrc(confidentialAPI.payslipPdfInlineUrl(selectedId, res.data.token));
      } catch (err) {
        if (!cancelled) {
          setError(true);
          setIframeSrc(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadInline();
    return () => { cancelled = true; };
  }, [isOpen, selectedId]);

  const selectedWorker = sortedWorkers.find((w) => w.id === selectedId) || null;

  const handleDownload = async () => {
    if (!selectedWorker || downloading) return;
    setDownloading(true);
    try {
      const res = await confidentialAPI.previewPayslipPdf(selectedWorker.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Payslip_Preview_${selectedWorker.staff_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Preview PDF downloaded');
    } catch (err) {
      toast.error("Couldn't download this payslip");
    } finally {
      setDownloading(false);
    }
  };

  const footer = (
    <>
      <button type="button" className="btn btn-admin-outline" onClick={onClose}>
        Close
      </button>
      <button
        type="button"
        className="btn btn-admin-primary"
        onClick={handleDownload}
        disabled={!iframeSrc || loading || downloading}
      >
        <Download className="w-4 h-4 mr-2" />
        {downloading ? 'Downloading…' : 'Download This Preview'}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={(
        <span className="inline-flex flex-wrap items-center gap-2 min-w-0">
          <span>Payslip Preview</span>
          <InfoBadge
            color="rose"
            size="xs"
            label="Confidential"
            className="flex-shrink-0"
            title="Information Classification Policy. Confidential is the highest marking (Level 3). The payslip PDF carries that marking."
          />
        </span>
      )}
      size="xl"
      theme="admin"
      footer={footer}
    >
      <div className="space-y-3">
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <FileText className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-800">
            This is exactly what the worker will see and download once you release payslips.
            Each payslip is marked Confidential. Pick any worker to spot-check before releasing.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <label htmlFor="payslip-preview-worker" className="text-sm font-medium text-gray-700 flex-shrink-0">
            Worker
          </label>
          <select
            id="payslip-preview-worker"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="select-admin text-sm w-full sm:max-w-md"
          >
            {sortedWorkers.length === 0 && <option value="">No workers in this run</option>}
            {sortedWorkers.map((w) => (
              <option key={w.id} value={w.id}>
                {w.staff_id} — {w.name}
              </option>
            ))}
          </select>
        </div>

        <div className="relative bg-gray-100 rounded-lg border border-gray-200 overflow-hidden" style={{ height: '70vh' }}>
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 z-10 bg-gray-100/80">
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              <p className="text-sm">Generating payslip…</p>
            </div>
          )}
          {error && !loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 z-10">
              <AlertTriangle className="w-6 h-6 text-amber-500 mb-2" />
              <p className="text-sm text-gray-700">Couldn't generate this payslip.</p>
              <p className="text-xs text-gray-500 mt-1">
                The record may not be calculated yet, or PDF generation failed. Try another worker.
              </p>
            </div>
          )}
          {iframeSrc && !error && (
            <PdfViewer
              key={iframeSrc}
              src={iframeSrc}
              title="Payslip preview"
              fileName={selectedWorker ? `Payslip_Preview_${selectedWorker.staff_id}.pdf` : 'payslip.pdf'}
              onDownload={handleDownload}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PayslipPreviewModal;
